import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import FilterCheckbox from '@/common/components/FilterCheckbox'
import ListFilters from '@/common/components/ListFilters'
import ListHeader from '@/common/components/ListHeader'
import Table from '@/common/components/Table'
import TextField from '@/common/components/TextField'
import { DatePickerWithRange } from '@/common/components/ui/dateRangePicker'
import { billingBillStatus } from '@/common/constants/statusBills.constants'
import useDebounce from '@/common/hooks/useDebounce'
import { useFilterDate } from '@/common/hooks/useFilterDate'
import { useFilterParams } from '@/common/hooks/useFilterParams'
import { Mapper } from '@/common/services/mapper'
import { CompanyBranchData } from '@/common/types/companyBranches.type'
import { Option } from '@/common/types/option'
import { defaultColumns } from '@/features/clientBills/constants/clientBills.constant'
import { useClientBills } from '@/features/clientBills/hooks/useClientBills'
import { ClientBillsTransformer } from '@/features/clientBills/services/clientBillsTransformer'
import { ClientBillData, ClientBillTableItem } from '@/features/clientBills/types/clientBills.type'
import { useCompanies } from '@/features/companies/hooks/useCompanies'
import { CompaniesTransformer } from '@/features/companies/services/companiesTransformer'
import { useAvailableCompanyBranches } from '@/features/privateCompanies/hooks/companyBranches/useAvailableCompanyBranches'
import { ArrowDownToLineIcon, SearchIcon } from 'lucide-react'
import { useEffect } from 'react'
import { options as kindOptions } from "../../../companies/constants/kind.constant";
import { useClientBillsTotalizer } from '../../hooks/useClientBillsTotalizer'
import { CompanyMovementsTotalizerData } from '@/features/privateCompanies/types/companyMovementsTotalizer.type'
import ResumeList from '@/features/privateCompanies/pages/Totals/components/ResumeList'
import useUserPermissions from '@/common/hooks/useUserPermissions'

const List = () => {
  const { filterParams, setFilterParams } = useFilterParams({
    status: billingBillStatus.find(({ value }) => value === 'pending_payment')
  });
  const { date, formattedDate } = useFilterDate(filterParams)

  const [search, setSearch] = useDebounce(filterParams.search || '')
  const [searchClient, setSearchClient] = useDebounce('')
  const [searchClientBranch, setSearchClientBranch] = useDebounce('')

  const {
    data: clientBills,
    meta,
    isPending: isPendingClientBills,
    error: clientBillsError = {} as any,
    refetch: refetchClientBills,
  } = useClientBills<ClientBillTableItem[]>({
    filters: {
      search,
      page: filterParams.page,
      clientId: filterParams.client?.value,
      branchId: filterParams.clientBranch?.value,
      kind: filterParams.kind?.value,
      status: filterParams.status?.value,
      startDate: formattedDate.from,
      endDate: formattedDate.to,
      showDescendants: filterParams.showDescendants || undefined,
      requiresAttention: filterParams.requiresAttention || undefined
    },
    mapper: Mapper.mapWithTransformer<ClientBillData[], ClientBillTableItem[]>({
      transformer: ClientBillsTransformer.tableTransformer(),
    }),
  })

  const { data: billsTotalizer } = useClientBillsTotalizer({
    filters: {
      clientId: filterParams.client?.value,
      branchId: filterParams.clientBranch?.value,
      kind: filterParams.kind?.value,
      status: filterParams.status?.value,
      openedAt: formattedDate.from,
      closedAt: formattedDate.to,
      showDescendants: filterParams.showDescendants || undefined,
      requiresAttention: filterParams.requiresAttention || undefined
    },
    mapper: Mapper.default<CompanyMovementsTotalizerData>(),
  })

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions()
  const userHasPermissionToListClientBillsTotalizers =
    isUserPermissionsPending || hasUserPermission('Admin::Billing::Bills::ClientBillsTotalizer')

  const { data: companyOptions } = useCompanies({
    filters: {
      search: searchClient,
    },
    mapper: Mapper.mapWithTransformer({ transformer: CompaniesTransformer.optionsTransformer() }),
  });

  const { data: companyBranchesOptions } = useAvailableCompanyBranches({
    companyId: filterParams.client?.value,
    filters: {
      search: searchClientBranch,
    },
    mapper: Mapper.mapToOptions<CompanyBranchData>({ valueFieldName: 'id', labelFieldName: 'display_name' })
  })

  useEffect(() => {
    if (!filterParams.clientBranch?.value) setFilterParams((prev) => ({ ...prev, showDescendants: null }))
  }, [filterParams.clientBranch?.value])

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] gap-6">
      <ListHeader Icon={<ArrowDownToLineIcon className="size-10" strokeWidth={1} />} title="Faturas (A Receber)" />

      <ErrorMessageWrapper
        className="h-[calc(100vh-128px)]"
        statusCode={clientBillsError?.status}
        refetchFunction={refetchClientBills}
      >
        <div className="flex flex-col flex-1 overflow-auto gap-6">
          <TextField
            LeftIcon={<SearchIcon />}
            placeholder="Pesquisar"
            className="rounded-sm shadow-[4px_0px_16px_0px_rgba(0,_0,_0,_0.5)]"
            onChange={(e) => {
              setSearch((e.target as HTMLInputElement).value)
              setFilterParams((prev) => ({ ...prev, search: (e.target as HTMLInputElement).value }))
            }}
            value={filterParams.search}
          />

          <div className="flex items-center gap-2 flex-wrap">
            <DatePickerWithRange date={date} setDate={(date) => setFilterParams((params) => ({ ...params, date }))} />

            <ListFilters
              filters={[
                {
                  className: 'w-56',
                  onChange: (status) => setFilterParams((prev) => ({ ...prev, status })),
                  value: filterParams.status,
                  options: [{ label: 'Todas', value: '' }, ...(billingBillStatus || [])],
                  placeholder: 'Status',
                  dataTestid: 'status',
                },
                {
                  className: 'w-56',
                  onChange: (client) => setFilterParams((prev) => ({ ...prev, client, clientBranch: null })),
                  value: filterParams.client,
                  onInputChange: (value) => setSearchClient(value),
                  options: [{ label: 'Todas', value: '' }, ...((companyOptions || []) as Option[])],
                  placeholder: 'Empresa',
                },
                {
                  className: `w-56 ${!filterParams.client?.value ? 'opacity-70' : ''}`,
                  onChange: (clientBranch) => setFilterParams((prev) => ({ ...prev, clientBranch })),
                  value: filterParams.clientBranch || null,
                  onInputChange: setSearchClientBranch,
                  options: [{ label: 'Todas', value: '' }, ...((companyBranchesOptions || []) as Option[])],
                  placeholder: 'Filial da empresa',
                  disabled: !filterParams.client?.value,
                },
                {
                  className: 'w-44',
                  onChange: (e) => setFilterParams((prev) => ({ ...prev, kind: e })),
                  value: filterParams.kind,
                  options: [{ label: 'Todas', value: '' }, ...(kindOptions || [])],
                  placeholder: 'Tipo de empresa'
                },
              ]}
            />

            <FilterCheckbox
              id="showDescendants"
              name="showDescendants"
              label="Mostrar descendentes"
              isChecked={!!filterParams.showDescendants}
              onChange={(showDescendants) =>
                setFilterParams((prev) => ({ ...prev, showDescendants: showDescendants ? true : null }))
              }
              isDisabled={!filterParams.clientBranch?.value}
            />
            <FilterCheckbox
              id="requiresAttention"
              name="requiresAttention"
              label="Demandam atenção"
              isChecked={!!filterParams.requiresAttention}
              onChange={(requiresAttention) =>
                setFilterParams((prev) => ({ ...prev, requiresAttention: requiresAttention ? true : null }))
              }
            />
          </div>

          {userHasPermissionToListClientBillsTotalizers && (
            <ResumeList
              data={billsTotalizer}
              requiresAttention={filterParams.requiresAttention}
            />
          )}

          <Table
            className="max-h-[calc(100vh-260px)]"
            columns={defaultColumns}
            isLoadingData={isPendingClientBills}
            rows={clientBills || []}
            totalPages={meta.total_pages}
            totalCount={meta.total}
            hasPagination
            rowEmptyValue="--"
            page={filterParams.page ? Number(filterParams.page) : 1}
            setPage={(page) => setFilterParams((prev) => ({ ...prev, page }))}
          />
        </div>
      </ErrorMessageWrapper>
    </div>
  )
}

export default List
